// Auflegton und Wählton (Wunsch Simon/Dilsar, 19.10.2026): zwei klar verschiedene, leise Signale statt der SDK-Töne.
// - Auflegen: ein einzelner weicher Ton, tief und fallend, unter 0,5 s.
// - Wählen: eine sanfte, steigende Tonfolge (drei Töne) beim Start eines ausgehenden Anrufs.
// Die Twilio-eigenen Töne (`outgoing`, `disconnect`) sind aus (engine.ts), damit nichts doppelt klingt. Signale laufen
// nacheinander, nie übereinander: bei „Nächster Anruf“ erst der Auflegton, dann nach einer kurzen Pause der Wählton.
// Lautstärke (0 bis 1, Standard 0,5 = halb so laut wie bisher) gilt für diese Signale und den Wählton im Power Dialer
// (rufton.ts); Regler in Einstellungen > Telefonie, pro Browser gespeichert.
//
// Oben reine Logik (ohne Browser testbar), unten die WebAudio-Ausgabe.

export type SignalArt = "auflegen" | "waehlen";

export interface SignalTon {
  /** Beginn innerhalb des Signals */
  startMs: number;
  dauerMs: number;
  /** Frequenz am Anfang und am Ende (gleitend) */
  vonHz: number;
  bisHz: number;
  attackMs: number;
  releaseMs: number;
}

/** Spitzenwert bei Lautstärke 1 (wie der bisherige Wählton „Weich“); bei Standard 0,5 also die Hälfte */
export const SIGNAL_SPITZE = 0.06;
/** Pause zwischen zwei Signalen (Auflegen → Wählen) */
export const SIGNAL_PAUSE_MS = 120;

export const SIGNALE: Record<SignalArt, SignalTon[]> = {
  // Ein weicher, tiefer Ton, der von 392 Hz auf 262 Hz fällt (G4 → C4), 380 ms
  auflegen: [{ startMs: 0, dauerMs: 380, vonHz: 392, bisHz: 262, attackMs: 25, releaseMs: 220 }],
  // Drei sanfte, steigende Töne (C5, E5, G5), zusammen 520 ms
  waehlen: [
    { startMs: 0, dauerMs: 140, vonHz: 523, bisHz: 523, attackMs: 25, releaseMs: 90 },
    { startMs: 180, dauerMs: 140, vonHz: 659, bisHz: 659, attackMs: 25, releaseMs: 90 },
    { startMs: 360, dauerMs: 160, vonHz: 784, bisHz: 784, attackMs: 25, releaseMs: 110 },
  ],
};

export function signalDauerMs(art: SignalArt): number {
  return Math.max(...SIGNALE[art].map((t) => t.startMs + t.dauerMs));
}

/**
 * Warteschlange: Wann darf ein Signal beginnen, damit es das vorige nicht überlappt?
 * `belegtBis` = Ende des zuletzt eingeplanten Signals (ms, gleiche Uhr wie `jetzt`).
 */
export function signalPlanen(jetzt: number, belegtBis: number, art: SignalArt): { startInMs: number; belegtBis: number } {
  const start = belegtBis > jetzt ? belegtBis + SIGNAL_PAUSE_MS : jetzt;
  return { startInMs: start - jetzt, belegtBis: start + signalDauerMs(art) };
}

// ---- Lautstärke (pro Browser) ---------------------------------------------------------------------------------

const LAUTSTAERKE_SPEICHER = "crm.telefonie.tonLautstaerke";
export const LAUTSTAERKE_STANDARD = 0.5;

export function lautstaerkeBegrenzen(w: unknown): number {
  const n = typeof w === "number" ? w : Number(w);
  if (!Number.isFinite(n)) return LAUTSTAERKE_STANDARD;
  return Math.min(1, Math.max(0, Math.round(n * 100) / 100));
}

export function tonLautstaerkeLesen(): number {
  try {
    const roh = window.localStorage.getItem(LAUTSTAERKE_SPEICHER);
    return roh === null ? LAUTSTAERKE_STANDARD : lautstaerkeBegrenzen(roh);
  } catch {
    return LAUTSTAERKE_STANDARD;
  }
}

export function tonLautstaerkeSetzen(w: number): void {
  try {
    const n = lautstaerkeBegrenzen(w);
    if (n === LAUTSTAERKE_STANDARD) window.localStorage.removeItem(LAUTSTAERKE_SPEICHER);
    else window.localStorage.setItem(LAUTSTAERKE_SPEICHER, String(n));
  } catch {
    /* ohne Speicher gilt der Standard */
  }
}

/** Verstärkung des Power-Dialer-Wähltons (rufton.ts): 1 = so laut wie bis zum 19.10., Standard 0,5 */
export function ruftonFaktor(): number {
  return tonLautstaerkeLesen();
}

// ---- Ausgabe ---------------------------------------------------------------------------------------------------

let belegtBis = 0;
/** Lautsprecher (gleiches Gerät wie das Gespräch); setzt der TelefonieProvider */
let ausgang: () => string | null = () => null;

export function signalAusgangSetzen(f: () => string | null): void {
  ausgang = f;
}

/** Signal abspielen, hinter einem noch laufenden Signal eingereiht. Fehler (kein Audio) bleiben still. */
export function signalSpielen(art: SignalArt, lautstaerke: number = tonLautstaerkeLesen()): void {
  if (typeof window === "undefined" || lautstaerke <= 0) return;
  const plan = signalPlanen(Date.now(), belegtBis, art);
  belegtBis = plan.belegtBis;
  const spitze = SIGNAL_SPITZE * lautstaerke;
  window.setTimeout(() => void ausgeben(SIGNALE[art], spitze, art), plan.startInMs);
}

async function ausgeben(toene: SignalTon[], spitze: number, art: SignalArt): Promise<void> {
  try {
    const ctx = new AudioContext();
    const ziel = ctx.createMediaStreamDestination();
    const audio = new Audio() as HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> };
    audio.srcObject = ziel.stream;
    const id = ausgang();
    if (id && audio.setSinkId) await audio.setSinkId(id).catch(() => undefined);
    await ctx.resume().catch(() => undefined);
    const t0 = ctx.currentTime + 0.03;
    for (const t of toene) {
      const osz = ctx.createOscillator();
      const huelle = ctx.createGain();
      const a = t0 + t.startMs / 1000;
      const e = a + t.dauerMs / 1000;
      osz.type = "sine";
      osz.frequency.setValueAtTime(t.vonHz, a);
      if (t.bisHz !== t.vonHz) osz.frequency.exponentialRampToValueAtTime(t.bisHz, e);
      huelle.gain.setValueAtTime(0, a);
      huelle.gain.linearRampToValueAtTime(spitze, a + t.attackMs / 1000);
      huelle.gain.setValueAtTime(spitze, Math.max(a + t.attackMs / 1000, e - t.releaseMs / 1000));
      huelle.gain.linearRampToValueAtTime(0, e);
      osz.connect(huelle).connect(ziel);
      osz.start(a);
      osz.stop(e + 0.02);
    }
    try {
      document.documentElement.dataset.signalton = art;
    } catch {
      /* egal */
    }
    void audio.play().catch(() => undefined);
    const ende = Math.max(...toene.map((t) => t.startMs + t.dauerMs)) + 200;
    window.setTimeout(() => {
      try {
        audio.pause();
        audio.srcObject = null;
        void ctx.close();
        if (document.documentElement.dataset.signalton === art) delete document.documentElement.dataset.signalton;
      } catch {
        /* egal */
      }
    }, ende);
  } catch {
    /* ohne Audio kein Signal */
  }
}
